// src/features/goal/types/objective.ts
// Objective 系統的類型定義
//
// 重構記錄 (2025-10-28):
// - 移除 category, priority, description, tags 欄位（極簡化設計）
// - 創建流程只需標題即可
// - Active 限制為最多 3 個
//
// 更新記錄 (2025-10-29):
// - 重新加入 category 欄位（Apple 風格設計）
// - Category 為可選，用於視覺識別和分類

import { Timestamp } from 'firebase/firestore';
import {
  Briefcase,
  Heart,
  GraduationCap,
  DollarSign,
  Users,
  Palette,
  Home,
  type LucideIcon
} from 'lucide-react';

/**
 * Objective 狀態
 */
export type ObjectiveStatus = 'active' | 'paused' | 'completed' | 'archived';

/**
 * Objective 分類
 *
 * Apple 風格設計：用於視覺識別和快速分類
 * - 可選欄位（預設 'growth'）
 * - Icon only 顯示（不顯示文字標籤）
 * - 淡色系配色，易於識別
 *
 * 新的 8 個分類（按使用頻率排序）：
 * - work, health, growth, finance （高頻）
 * - romance, social, leisure, environment （中低頻）
 *
 * Legacy 分類（僅用於顯示舊資料）：
 * - family （保留向後兼容）
 */
export type ObjectiveCategory =
  | 'work'
  | 'health'
  | 'growth'
  | 'finance'
  | 'romance'
  | 'social'
  | 'leisure'
  | 'environment'
  | 'family';  // Legacy only

/**
 * 12週 Objective 資料結構
 *
 * 設計理念：
 * - 極簡設計：標題即本質，無需描述或分類
 * - 用於進度追蹤和 Ripples 累積
 * - 與 L1 Objective Memory 通過 linkedObjectiveId 關聯（複本快照策略）
 * - 儲存位置：users/{userId}/objectives/{objectiveId}
 * - Active 限制：最多 3 個活躍目標
 */
export interface Objective {
  // 基本資訊
  id: string;
  userId: string;
  title: string;  // 目標標題（20-80字） - 唯一的描述性欄位
  category?: ObjectiveCategory;  // 分類（可選，預設 'personal'）

  // 狀態
  status: ObjectiveStatus;

  // L1 連結（複本快照策略）
  linkedObjectiveId?: string;  // 連結到 L1 Objective Memory 的 memoryId（可選）

  // 進度追蹤
  totalRipples: number;        // 累積漣漪值
  weeklyRipples: number[];     // 每週漣漪值（最多12個）
  currentWeek: number;         // 當前是第幾週（1-12+）

  // 時間追蹤
  startDate: Timestamp;        // 開始日期
  targetDate?: Timestamp;      // 目標完成日期（可選，通常是12週後）
  completedAt?: Timestamp;     // 完成時間（status=completed時設置）
  lastProgressAt?: Timestamp;  // 最後推進時間

  // 統計資訊
  linkedTasksCount: number;    // 關聯的任務數量

  // 系統欄位
  createdAt: Timestamp;
  updatedAt: Timestamp;

  // AI 相關（未來使用）
  confidence?: number;         // AI 建議信心度（0-1）
  source?: 'l1_memory' | 'user_created' | 'ai_suggested';
}

/**
 * 創建 Objective 請求（極簡化）
 */
export interface CreateObjectiveRequest {
  title: string;                 // 唯一必填欄位
  category?: ObjectiveCategory;  // 分類（可選，預設 'personal'）
  status?: ObjectiveStatus;      // 由前端根據 Active 數量決定，預設 'active'
  linkedObjectiveId?: string;    // 如果從 L1 創建，提供 memoryId
  targetDate?: Date;             // 目標完成日期（可選，預設 12 週後）
}

/**
 * 更新 Objective 請求
 */
export interface UpdateObjectiveRequest {
  title?: string;
  status?: ObjectiveStatus;
  targetDate?: Date;
}

/**
 * Objective 列表項（輕量版，用於列表顯示）
 */
export interface ObjectiveListItem {
  id: string;
  title: string;
  status: ObjectiveStatus;
  totalRipples: number;
  currentWeek: number;
  linkedTasksCount: number;
  lastProgressAt?: Timestamp;
}

/**
 * Objective 查詢選項
 */
export interface ObjectiveQueryOptions {
  status?: ObjectiveStatus | ObjectiveStatus[];
  limit?: number;
  orderBy?: 'createdAt' | 'totalRipples' | 'lastProgressAt';
  orderDirection?: 'asc' | 'desc';
}

/**
 * L1 Objective Memory（從 L1 記憶系統查詢的目標）
 * 用於創建新 Objective 時選擇來源
 *
 * 注意：L1 Memory 系統保留 category 和 priority，
 * 但創建 Objective 時這些資訊不會被使用
 */
export interface L1ObjectiveMemory {
  id: string;
  content: string;
  category: string;      // L1 系統保留，但 Objectives 不使用
  priority: string;      // L1 系統保留，但 Objectives 不使用
  tags: string[];
  confidence: number;
  createdAt: Timestamp;
}

/**
 * Ripples 計算結果
 */
export interface RipplesCalculation {
  baseScore: number;     // 基礎分數（來自 energyLevel）
  multipliers: {
    firstProgress?: number;   // 首次推進加成（1.5x）
    streak?: number;          // 連續推進加成（1.5x）
    relevance?: number;       // 相關性加成（1.0x-2.0x）
  };
  totalRipples: number;  // 最終漣漪值
  message?: string;      // 鼓勵訊息（如 "Streak bonus! Keep the focus!"）
}

// ============================================================================
// 常量定義
// ============================================================================

/**
 * 預設的 12 週目標期限（天數）
 */
export const DEFAULT_OBJECTIVE_DURATION_DAYS = 84; // 12 weeks * 7 days

/**
 * Objective 狀態標籤配置
 * 用於 UI 顯示狀態徽章
 */
export const OBJECTIVE_STATUS_CONFIG: Record<ObjectiveStatus, {
  label: string;
  color: string;
  bgColor: string;
}> = {
  active: {
    label: 'Active',
    color: 'text-green-600',
    bgColor: 'bg-green-50'
  },
  paused: {
    label: 'Paused',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50'
  },
  completed: {
    label: 'Completed',
    color: 'text-sky-600',
    bgColor: 'bg-sky-50'
  },
  archived: {
    label: 'Archived',
    color: 'text-gray-400',
    bgColor: 'bg-gray-50'
  }
};

/**
 * Objective 分類配置（Apple 風格）
 *
 * 設計原則：
 * - Icon only（不顯示文字標籤）
 * - 彩色背景 + 深色 Icon（易於識別）
 * - 每個 category 有獨特的配色方案
 * - isLegacy: true 的分類不出現在創建 UI，僅用於顯示舊資料
 */
export const OBJECTIVE_CATEGORY_CONFIG: Record<ObjectiveCategory, {
  label: string;
  icon: LucideIcon;
  bgColor: string;      // 背景色（淺色）
  iconColor: string;    // Icon 顏色（深色）
  isLegacy?: boolean;   // 是否為 legacy 分類
}> = {
  // 新的 8 個分類（按使用頻率排序）
  work: {
    label: 'Work',
    icon: Briefcase,
    bgColor: 'bg-sky-50',
    iconColor: 'text-sky-600'
  },
  health: {
    label: 'Health',
    icon: Heart,
    bgColor: 'bg-rose-50',
    iconColor: 'text-rose-600'
  },
  growth: {
    label: 'Growth',
    icon: GraduationCap,
    bgColor: 'bg-sky-50',
    iconColor: 'text-sky-600'
  },
  finance: {
    label: 'Finance',
    icon: DollarSign,
    bgColor: 'bg-green-50',
    iconColor: 'text-green-600'
  },
  romance: {
    label: 'Romance',
    icon: Heart,  // 使用 Heart icon，與 health 區分（可考慮後續換 icon）
    bgColor: 'bg-pink-50',
    iconColor: 'text-pink-600'
  },
  social: {
    label: 'Social',
    icon: Users,
    bgColor: 'bg-orange-50',
    iconColor: 'text-orange-600'
  },
  leisure: {
    label: 'Leisure',
    icon: Palette,
    bgColor: 'bg-cyan-50',
    iconColor: 'text-cyan-600'
  },
  environment: {
    label: 'Environment',
    icon: Home,
    bgColor: 'bg-amber-50',
    iconColor: 'text-amber-600'
  },

  // Legacy 分類（保留向後兼容，不顯示在創建 UI）
  family: {
    label: 'Family',
    icon: Home,
    bgColor: 'bg-amber-50',
    iconColor: 'text-amber-600',
    isLegacy: true
  }
};

/**
 * Ripples 計算配置
 */
export const RIPPLES_CONFIG = {
  // 基礎分數對應表（來自 Task.energyLevel）
  baseScores: {
    low: 3,       // energyLevel 1-2
    medium: 6,    // energyLevel 3-4
    high: 10,     // energyLevel 5
    default: 6    // 無 energyLevel 時預設
  },

  // 加成倍率
  multipliers: {
    firstProgress: 1.5,      // 首次推進
    streak: 1.5,             // 連續推進（48小時內）
    coreRelevance: 2.0,      // 核心相關（高相關性）
    baseRelevance: 1.0       // 基礎相關
  },

  // 連續推進判定時間（毫秒）
  streakTimeWindow: 48 * 60 * 60 * 1000  // 48 hours
};
